import { Field, Input, Label } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { handleEnterSpaceKey } from "../../../../app/keyboardUtils";

export interface DistrictAddressInfoSectionProps {
	value: {
		address: string;
		city: string;
		state: string;
		zip: string;
		emailAddress: string;
		phone: string;
	};
	onAddressChange: (
		patch: Partial<{
			address: string;
			city: string;
			state: string;
			zip: string;
			emailAddress: string;
			phone: string;
		}>
	) => void;
	editMode?: boolean;
	showAddressSection: boolean;
	toggleShowAddressSection: () => void;
}

export default function DistrictAddressInfoSection({
	value,
	onAddressChange,
	showAddressSection,
	toggleShowAddressSection,
	editMode,
}: DistrictAddressInfoSectionProps) {
	return (
		<div className="mt-2">
			<div className="flex items-center cursor-pointer" onClick={toggleShowAddressSection}>
				{showAddressSection ? (
					<ChevronDownIcon
						tabIndex={0}
						className="size-5 hover:cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							toggleShowAddressSection();
						}}
						onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
					/>
				) : (
					<ChevronRightIcon
						tabIndex={0}
						className="size-5 hover:cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							toggleShowAddressSection();
						}}
						onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
					/>
				)}
				<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
					Address and Contact Information
				</h3>
			</div>
			{showAddressSection && (
				<div className="flex gap-x-12 w-full">
					<div className="flex-col w-3/5">
						<h2 className="mb-2">Address</h2>
						<div
							className={`${editMode ? "md:grid-cols-9 gap-y-4" : "gap-y-2"} grid grid-cols-2 gap-x-6`}>
							<Field
								className={`${editMode ? "col-span-2 md:col-span-full" : "col-span-full"}`}>
								<Label htmlFor="line1" className="font-medium text-sm">
									Street Address
								</Label>
								<Input
									id="line1"
									name="line1"
									type="text"
									maxLength={255}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.address ?? ""}
									onChange={(e) => {
										onAddressChange({ address: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={`${editMode ? "col-span-2 md:col-span-5 xl:col-span-6" : "col-span-full"}`}>
								<Label htmlFor="city" className="font-medium text-sm">
									City
								</Label>
								<Input
									id="city"
									name="city"
									type="text"
									maxLength={50}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.city ?? ""}
									onChange={(e) => {
										onAddressChange({ city: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={`${editMode ? "col-span-1 md:col-span-2 xl:col-span-1" : "col-span-1"}`}>
								<Label htmlFor="state" className="font-medium text-sm">
									State
								</Label>
								<Input
									id="state"
									name="state"
									type="text"
									maxLength={3}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.state ?? ""}
									onChange={(e) => {
										onAddressChange({ state: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={`${editMode ? "col-span-1 md:col-span-2" : "col-span-1"}`}>
								<Label htmlFor="zip" className="font-medium text-sm">
									ZIP Code
								</Label>
								<Input
									id="zip"
									name="zip"
									type="text"
									maxLength={10}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.zip ?? ""}
									onChange={(e) => {
										onAddressChange({ zip: e.target.value });
									}}
								/>
							</Field>
						</div>
					</div>
					<div className="flex-col w-2/5">
						<h2 className="mb-2">Contact Information</h2>
						<div
							className={`${editMode ? "gap-y-4 sm:grid-cols-6" : "gap-y-2"} grid grid-cols-1`}>
							<Field className={`${editMode ? "col-span-5" : "col-span-full"}`}>
								<Label htmlFor="email" className="font-medium text-sm">
									Email
								</Label>
								<Input
									id="email"
									name="email"
									type="text"
									maxLength={255}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.emailAddress ?? ""}
									onChange={(e) => {
										onAddressChange({ emailAddress: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={`${editMode ? "col-span-4 lg:col-span-3" : "col-span-full"}`}>
								<Label htmlFor="phone" className="font-medium text-sm">
									Phone
								</Label>
								<Input
									id="phone"
									name="phone"
									type="text"
									maxLength={14}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.phone ?? ""}
									onChange={(e) => {
										onAddressChange({ phone: e.target.value });
									}}
								/>
							</Field>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
